import React, { useEffect, useId, useMemo, useRef } from 'react';
import { useAimHeatmapCell } from '../lib/aimQueries';
import ProviderColorDot from '../primitives/ProviderColorDot';
import AimEmpty, { AimLoading, AimError } from '../primitives/AimEmpty';
import useFocusTrap from '../../../hooks/useFocusTrap';
import { prettifyCategory } from '../lib/categoryLabel';
import { prettifyProviderLabel } from '../lib/providerColors';
import { heatmapCellView, HEATMAP_CELL_LIMIT } from '../lib/heatmapCell';
import { isPending } from '../lib/queryState';

/** A mention rate as the heatmap shows it: whole when whole, one decimal otherwise. */
const fmtPct = (pct) => (Number.isInteger(Number(pct)) ? String(Number(pct)) : Number(pct).toFixed(1));

/**
 * Drill-down behind one heatmap cell: the responses a provider gave to a category's prompts.
 *
 * The summary reads the drill's own counts: answers that name the client out of the cell's
 * responses, and the rate. The rows are one per answer and capped, so the caption says when they
 * cover fewer responses than the cell counts.
 */
export default function HeatmapCellPanel({
  clientId, runId, category, providerName, onClose,
}) {
  const dialogRef = useRef(null);
  const closeBtnRef = useRef(null);
  const previouslyFocusedRef = useRef(null);
  const titleId = useId();
  useFocusTrap(dialogRef);

  const query = useAimHeatmapCell(clientId, {
    category, provider: providerName, runId, limit: HEATMAP_CELL_LIMIT,
  });
  const view = useMemo(() => heatmapCellView(query.data), [query.data]);
  const hasCounts = view.responses != null && view.mentioned != null && view.mentionPct != null;
  const hasSummary = hasCounts || view.avgPosition != null || view.topCompetitors.length > 0;
  // The rows can cover fewer responses than the cell counts (the row cap cut them off); say so
  // rather than let the caption read as the cell's total. More responses than the cell counts is
  // not a shortfall, so that case just shows the plain count.
  const countsDiffer = view.responses != null && view.responseCount < view.responses;

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement;
    closeBtnRef.current?.focus();
    return () => {
      const prev = previouslyFocusedRef.current;
      if (prev && typeof prev.focus === 'function') {
        requestAnimationFrame(() => prev.focus());
      }
    };
  }, []);

  useEffect(() => {
    const onKey = (e) => { if (e.key === 'Escape') onClose?.(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  return (
    <div
      ref={dialogRef}
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      className="fixed inset-0 z-40 flex"
    >
      <button
        type="button"
        className="flex-1 bg-black/40 cursor-default"
        aria-label="Close panel"
        onClick={onClose}
      />
      <aside className="w-full sm:w-[920px] bg-white dark:bg-gray-900 shadow-2xl border-l border-gray-200 dark:border-gray-800 overflow-y-auto">
        <header className="sticky top-0 z-10 px-5 py-3 flex items-center justify-between border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <h2 id={titleId} className="text-base font-semibold text-gray-900 dark:text-white">
            {prettifyCategory(category)} × {prettifyProviderLabel(providerName)} — Responses
          </h2>
          <button
            ref={closeBtnRef}
            type="button"
            onClick={onClose}
            className="text-gray-500 hover:text-gray-900 dark:hover:text-white text-sm"
            aria-label="Close"
          >
            ✕
          </button>
        </header>

        <div className="p-5 space-y-4">
          {hasSummary && (
            <div data-testid="heatmap-cell-summary" className="flex flex-wrap items-baseline gap-x-5 gap-y-1 text-sm text-gray-700 dark:text-gray-300">
              {hasCounts && (
                <span className="font-semibold text-gray-900 dark:text-white">
                  {view.mentioned} of {view.responses} mentioned · {fmtPct(view.mentionPct)}%
                </span>
              )}
              {view.avgPosition != null && <span>Avg position {view.avgPosition.toFixed(1)}</span>}
              {view.topCompetitors.length > 0 && (
                <span>
                  Top competitors:{' '}
                  {view.topCompetitors
                    .map((c) => (c.appearances != null ? `${c.park_name} ×${c.appearances}` : c.park_name))
                    .join(', ')}
                </span>
              )}
            </div>
          )}

          {isPending(query) ? (
            <AimLoading height={160} />
          ) : query.error ? (
            <AimError message="Couldn't load the responses for this cell." onRetry={query.refresh} />
          ) : view.rows.length === 0 ? (
            <AimEmpty message="No responses for this category × provider." />
          ) : (
            <>
              <div className="overflow-x-auto responsive-card-table-wrapper">
                <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[640px] responsive-card-table">
                  <caption className="caption-top text-left pb-2 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    Responses{' '}
                    <span className="font-normal normal-case text-gray-400">
                      · {view.responseCount}{countsDiffer ? ` of ${view.responses} listed` : ''}
                    </span>
                  </caption>
                  <thead className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    <tr>
                      <th className="text-left py-2">Provider</th>
                      <th className="text-left py-2">Prompt</th>
                      <th className="text-left py-2">Mention</th>
                      <th className="text-right py-2">Position</th>
                      <th className="text-left py-2">Recommendation</th>
                    </tr>
                  </thead>
                  <tbody>
                    {view.rows.map((r, i) => (
                      <tr key={`${r.response_id ?? 'row'}-${i}`} className="border-t border-gray-100 dark:border-gray-800">
                        <td className="py-2" data-label="Provider">
                          <span className="inline-flex items-center gap-1.5">
                            <ProviderColorDot displayName={providerName} />
                            <span>{prettifyProviderLabel(providerName)}</span>
                          </span>
                        </td>
                        <td className="py-2 truncate max-w-[360px]" title={r.prompt_text} data-label="Prompt">{r.prompt_text || '—'}</td>
                        <td className="py-2" data-label="Mention">
                          {r.errored
                            ? <span className="font-semibold text-red-600 dark:text-red-400">Error</span>
                            : r.client_mentioned ? '✓ Client' : '—'}
                        </td>
                        <td className="py-2 text-right tabular-nums" data-label="Position">{r.list_position != null ? r.list_position : '—'}</td>
                        <td className="py-2 text-gray-600 dark:text-gray-400" data-label="Recommendation">
                          {r.recommendation_strength ? prettifyCategory(r.recommendation_strength) : '—'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              {view.truncated && (
                <p className="text-[11px] text-gray-500 dark:text-gray-400">
                  Showing the first {HEATMAP_CELL_LIMIT} rows.
                </p>
              )}
            </>
          )}
        </div>
      </aside>
    </div>
  );
}
